// Types + logique pure du quiz (utilisables côté client).
// Le chargement du YAML se fait dans quiz.server.ts (serveur uniquement).

export type QuizOption = {
  value: string;
  label: string;
  score?: number;
  disqualify?: boolean;
};

export type QuizQuestion = {
  id: string;
  question: string;
  help?: string;
  type: "single" | "multi";
  tags?: string[];
  options: QuizOption[];
};

export type QuizSection = {
  id: string;
  title: string;
  subtitle?: string;
  questions: QuizQuestion[];
};

export type QuizConfig = {
  meta: {
    title: string;
    subtitle: string;
    ctaStart: string;
    resultsDelivery: string;
  };
  contact: { intro: string; consent: string };
  scoring: { qualifiedThreshold: number; standardThreshold: number };
  sections: QuizSection[];
};

// Réponses : id de question -> valeur (single) ou valeurs (multi).
export type Answers = Record<string, string | string[]>;

export const flattenQuestions = (cfg: QuizConfig): QuizQuestion[] =>
  cfg.sections.flatMap((s) => s.questions);

export const totalQuestions = (cfg: QuizConfig): number =>
  flattenQuestions(cfg).length;

const selectedValues = (a: string | string[] | undefined): string[] =>
  a === undefined ? [] : Array.isArray(a) ? a : [a];

export type QuizResult = {
  score: number;
  disqualified: boolean;
  tier: "prioritaire" | "standard" | "nurture" | "hors_cible";
};

// Calcule score + disqualification à partir des réponses.
export function evaluate(cfg: QuizConfig, answers: Answers): QuizResult {
  let score = 0;
  let disqualified = false;

  for (const q of flattenQuestions(cfg)) {
    const chosen = selectedValues(answers[q.id]);
    for (const opt of q.options) {
      if (!chosen.includes(opt.value)) continue;
      if (opt.disqualify) disqualified = true;
      if (typeof opt.score === "number") score += opt.score;
    }
  }

  if (disqualified) return { score, disqualified: true, tier: "hors_cible" };

  const { qualifiedThreshold, standardThreshold } = cfg.scoring;
  const tier =
    score >= qualifiedThreshold
      ? "prioritaire"
      : score >= standardThreshold
        ? "standard"
        : "nurture";

  return { score, disqualified: false, tier };
}
